// client/src/components/influencer/InfluencerSidebar.js
// config 기반 사이드바 — 마케터 확정 패턴(MarketerSidebar) 미러, mb-* 클래스 재사용(ib-theme 민트)
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import '../../styles/marketer/MarketerSidebar.css';

// SVG Icons
const ChevronDown = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24">
    <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z" />
  </svg>
);

// 그룹 구성 v4 시안 확정(2026-08-05): 프로젝트 → 포인트 → 내 정보 → 고객센터
const MENU_GROUPS = [
  {
    key: 'project',
    label: '프로젝트',
    items: [
      ['projectApply', '프로젝트 지원하기'],
      ['projectApplyCheck', '지원 중인 프로젝트'],
      ['projectContract', '선정된 프로젝트 계약'],
      ['resultSend', '결과물 제출하기'],
    ],
  },
  {
    key: 'point',
    label: '포인트',
    items: [
      ['pointList', '포인트 내역 보기'],
      ['pointExchange', '포인트 환전 신청'],
    ],
  },
  {
    key: 'my',
    label: '내 정보',
    items: [
      ['snsView', 'SNS 계정 · 단가'],
      ['snsAdd', 'SNS 계정 추가'],
      ['bank', '통장 등록 · 변경'],
      ['contract', '프리랜서 계약'],
      ['profile', '내 정보 변경'],
      ['password', '비밀번호 변경'],
    ],
  },
  {
    key: 'inquiry',
    label: '고객센터',
    items: [
      ['inquiryCheck', '진행 중인 문의'],
      ['inquiryFinish', '완료된 문의'],
    ],
  },
];

// sub → 소속 그룹 (URL만 보고 열림 상태 동기화)
const SUB_TO_GROUP = MENU_GROUPS.reduce((acc, g) => {
  g.items.forEach(([sub]) => { acc[sub] = g.key; });
  return acc;
}, {});

const InfluencerSidebar = ({ isOpen, onClose, showSnsBadge }) => {
  const nav = useNavigate();
  const location = useLocation();
  const [openSection, setOpenSection] = useState(null);

  // URL 파라미터 기반 열림 상태 동기화
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const group = SUB_TO_GROUP[params.get('sub')];
    if (group) setOpenSection(group);
  }, [location.search]);

  const toggleSection = (section) => {
    setOpenSection(openSection === section ? null : section);
  };

  const closeOnMobile = () => {
    if (window.innerWidth <= 1024 && onClose) {
      onClose();
    }
  };

  const handleMenuClick = (sub) => {
    nav(`/influencer/mybiz?sub=${sub}`);
    closeOnMobile();
  };

  const params = new URLSearchParams(location.search);
  const currentSub = params.get('sub');
  const isActive = (sub) => currentSub === sub;
  const isHomeActive = !currentSub || currentSub === 'home';

  return (
    <div className={`mb-sidebar-area ${isOpen ? 'open' : ''}`}>
      <div className="mb-sidebar">
        {/* 프로필 영역 */}
        <div className="mb-sidebar-profile">
          <h3>인플루언서 센터</h3>
          <p>Influencer Workspace</p>
        </div>

        {/* 네비게이션 */}
        <nav className="mb-sidebar-nav">

          {/* 활동 홈 */}
          <div className="mb-nav-group">
            <button
              className={`mb-menu-item mb-menu-item--single ${isHomeActive ? 'active' : ''}`}
              onClick={() => handleMenuClick('home')}
            >
              <span>활동 홈</span>
            </button>
          </div>

          {/* 접이식 그룹들 */}
          {MENU_GROUPS.map((group) => (
            <div className="mb-nav-group" key={group.key}>
              <button
                className={`mb-menu-item ${openSection === group.key ? 'active' : ''}`}
                onClick={() => toggleSection(group.key)}
              >
                <span>{group.label}</span>
                <ChevronDown className={`mb-menu-chevron ${openSection === group.key ? 'rotate' : ''}`} />
              </button>
              <div className={`mb-submenu ${openSection === group.key ? 'open' : ''}`}>
                <div className="mb-submenu__inner">
                  {/* 고객센터: 문의하기(작성)가 맨 위 */}
                  {group.key === 'inquiry' && (
                    <button
                      className="mb-submenu-item"
                      onClick={() => {
                        nav('/inquiry', { state: { role: 'influencer' } });
                        closeOnMobile();
                      }}
                    >
                      문의하기
                    </button>
                  )}
                  {group.items.map(([sub, label]) => (
                    <button
                      key={sub}
                      className={`mb-submenu-item ${sub === 'snsView' ? 'mb-submenu-item--flex' : ''} ${isActive(sub) ? 'active' : ''}`}
                      onClick={() => handleMenuClick(sub)}
                    >
                      {label}
                      {sub === 'snsView' && showSnsBadge && (
                        <span className="ib-warn-badge" title="희망단가 미설정">단가 미입력</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ))}

          {/* 서비스 안내 — 여백이 있으면 하단 고정(margin-top:auto), 위 그룹 확장 시엔 밀려나 스크롤로 접근 */}
          <div className="mb-nav-group mb-nav-group--bottom">
            <div className="mb-menu-item" style={{ cursor: 'default' }}>
              <span>서비스 안내</span>
            </div>
            <div className="mb-submenu open">
              <div className="mb-submenu__inner">
                <button
                  className="mb-submenu-item"
                  onClick={() => {
                    nav('/lp/influencer');
                    closeOnMobile();
                  }}
                >
                  인플루언서 안내
                </button>
                <button
                  className="mb-submenu-item"
                  onClick={() => window.open('/guide', '_blank', 'noopener,noreferrer')}
                >
                  서비스 가이드 ↗
                </button>
                <button
                  className="mb-submenu-item"
                  onClick={() => {
                    nav('/faq');
                    closeOnMobile();
                  }}
                >
                  FAQ
                </button>
              </div>
            </div>
          </div>
        </nav>
      </div>
    </div>
  );
};

export default InfluencerSidebar;
